// src/ThingsToDo.jsx
import sphereImg from './assets/things/strip-sphere.jpg';
import bellagioImg from './assets/things/bellagio-fountains.jpg';
import fontainebleauImg from './assets/things/fontainebleau.jpg';
import circaImg from './assets/things/circa-pool.jpg';
import redrockImg from './assets/things/redrock.jpg';
import mtcharlestonImg from './assets/things/mtcharleston.jpg';
import secretPizzaImg from './assets/things/secret-pizza.jpg';
import hellsKitchenImg from './assets/things/hells-kitchen.jpg';
import zionImg from './assets/things/zion.jpg';
import valleyFireImg from './assets/things/valley-of-fire.jpg';
import vegasBg from './assets/things/vegas-bg.jpg';
import PageWrapper from './components/PageWrapper';

const sections = [
  {
    emoji: '🎰',
    title: 'Strip Must-Sees',
    items: [
      'The Sphere – spectacular visuals & concerts',
      'Bellagio Fountain Show',
      'Caesars Palace & The Forum Shops',
      'The LINQ + High Roller Observation Wheel',
      'New York‑New York Roller Coaster',
    ],
    images: [{ src: sphereImg, alt: 'The Sphere' }, { src: bellagioImg, alt: 'Bellagio Fountains' }],
  },
  {
    emoji: '🛎️',
    title: 'New & Noteworthy Hotels',
    items: [
      'Fontainebleau Las Vegas – A luxurious revival of the Miami icon with modern flair',
      'Resorts World – Sleek, tech-savvy resort with three Hilton brands in one',
      'Circa Resort & Casino (21+) – Fremont\'s newest adult-only casino with Stadium Swim & a rooftop vibe',
    ],
    images: [{ src: fontainebleauImg, alt: 'Fontainebleau' }, { src: circaImg, alt: 'Circa Pool' }],
  },
  {
    emoji: '🌄',
    title: 'Outdoor Adventures',
    items: [
      'Red Rock Canyon National Conservation Area',
      'Mount Charleston & Lee Canyon – hiking, snowboarding, fresh air',
      'Lake Mead – boating, jet skis, beaches',
      'Lake Las Vegas – kayaking, golf, resort vibes',
      'Hoover Dam – engineering marvel',
    ],
    images: [{ src: redrockImg, alt: 'Red Rock Canyon' }, { src: mtcharlestonImg, alt: 'Mount Charleston' }],
  },
  {
    emoji: '🍽️',
    title: 'Food & Unique Bites',
    items: [
      'Secret Pizza (hidden inside The Cosmopolitan)',
      'Gordon Ramsay Hell\'s Kitchen',
      'Fremont Street Food Tour',
      'Black Tap – burgers & craft shakes',
      'Best Friend (Korean fusion at Park MGM)',
      'Hwaro – upscale Korean BBQ in Chinatown',
      'Sushi Neko – all-you-can-eat sushi in Chinatown',
      'Tiger Sugar – iconic boba drinks in Chinatown',
      'Hot N Juicy Crawfish – Cajun seafood boils with gloves and bibs',
    ],
    images: [{ src: secretPizzaImg, alt: 'Secret Pizza' }, { src: hellsKitchenImg, alt: 'Hell\'s Kitchen' }],
  },
];

const roadTrips = [
  { label: 'Zion National Park', time: '2.5 hrs', url: 'https://maps.google.com/maps?daddr=Zion+National+Park' },
  { label: 'Bryce Canyon National Park', time: '4 hrs', url: 'https://maps.google.com/maps?daddr=Bryce+Canyon+National+Park' },
  { label: 'Valley of Fire State Park', time: '1 hr', url: 'https://maps.google.com/maps?daddr=Valley+of+Fire+State+Park' },
  { label: 'Death Valley National Park', time: '2.5 hrs', url: 'https://maps.google.com/maps?daddr=Death+Valley+National+Park' },
  { label: 'Pahrump / Wine Country', time: '1 hr', url: 'https://maps.google.com/maps?daddr=Pahrump+Winery+NV' },
  { label: 'Seven Magic Mountains', time: '30 min', url: 'https://maps.google.com/maps?daddr=Seven+Magic+Mountains+Las+Vegas' },
  { label: 'Laughlin (Colorado River)', time: '1.5 hrs', url: 'https://maps.google.com/maps?daddr=Laughlin+NV' },
  { label: 'Joshua Tree NP', time: '4 hrs', url: 'https://maps.google.com/maps?daddr=Joshua+Tree+National+Park' },
];

export default function ThingsToDo() {
  return (
    <PageWrapper backgroundImage={vegasBg}>
      <div className="max-w-4xl mx-auto px-4 py-12 space-y-8 w-full">

        {/* Header */}
        <div className="pfr-card px-8 py-10 text-center fade-up">
          <p className="section-label">Your Vegas Experience</p>
          <h1 className="font-display text-5xl font-bold text-white mb-2">Cool Things to Do in Vegas</h1>
          <div className="gold-divider mb-5" />
          <p className="font-body text-lg mb-2" style={{ color: 'var(--accent)' }}>
            Whether you're here for the glitz, the desert views, or the food — Vegas has it all.
          </p>
          <p className="font-body text-gray-400 max-w-xl mx-auto">
            From towering light shows to red-rock canyons and adrenaline-filled rides, Las Vegas is full of unforgettable experiences.
          </p>
        </div>

        {/* Main Sections */}
        <div className="space-y-3 fade-up fade-up-1">
          {sections.map((section) => (
            <details key={section.title} className="pfr-accordion" open={section.title === 'Strip Must-Sees'}>
              <summary>
                <span className="text-xl">{section.emoji}</span>
                {section.title}
              </summary>
              <div className="pfr-accordion-body pt-4">
                <ul className="space-y-1.5 mb-5">
                  {section.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 font-body text-gray-300 text-sm">
                      <span style={{ color: 'var(--primary)' }} className="flex-shrink-0 mt-0.5">›</span>
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="grid grid-cols-2 gap-4">
                  {section.images.map(({ src, alt }) => (
                    <img key={alt} src={src} alt={alt} className="w-full h-36 sm:h-48 object-cover rounded-lg shadow-lg" />
                  ))}
                </div>
              </div>
            </details>
          ))}
        </div>

        {/* Road Trips */}
        <details className="pfr-accordion fade-up fade-up-2">
          <summary>
            <span className="text-xl">🛣️</span>
            Road Trips Near Vegas
          </summary>
          <div className="pfr-accordion-body pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
              {roadTrips.map(({ label, time, url }) => (
                <a
                  key={label}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex items-center justify-between pfr-card px-4 py-3 no-underline"
                >
                  <span className="font-body text-sm text-white group-hover:text-[#ffda3e] transition-colors">{label}</span>
                  <span className="font-body text-xs text-gray-400 ml-2 whitespace-nowrap">{time}</span>
                </a>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img src={zionImg} alt="Zion National Park" className="w-full h-36 sm:h-48 object-cover rounded-lg shadow-lg" />
              <img src={valleyFireImg} alt="Valley of Fire" className="w-full h-36 sm:h-48 object-cover rounded-lg shadow-lg" />
            </div>
          </div>
        </details>

      </div>
    </PageWrapper>
  );
}
